import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Button, Card, Popconfirm, Space, Table, Tag, Typography } from "antd";
import { RobotOutlined } from "@ant-design/icons";
import moment from "moment";
import Localize from "../Localized/Localize.comp";
import {
  archiveAiVerificationApi,
  getAiVerificationApi,
  verifyWithAiApi,
} from "../../api/dataQuality.api";

const { Text } = Typography;

const VERDICT_COLORS = {
  correct: "green",
  incorrect: "red",
  unverifiable: "default",
};
const COLORS = { correct: "#52c41a", incorrect: "#f5222d", found: "#1890ff" };

// Shared by the banner on top of a detail page and the hints next to its form fields
const AiContext = createContext({ hints: {} });

// For the form: what the AI found for each field (empty while the results are hidden)
export const useAiHints = () => useContext(AiContext).hints;

const isUrl = (v) => typeof v === "string" && /^https?:\/\//i.test(v);

const SourceLink = ({ url }) =>
  isUrl(url) ? (
    <a href={url} target="_blank" rel="noopener noreferrer">
      {Localize("PAGES.AI_VERIFICATION.SOURCE")}
    </a>
  ) : null;

// What the AI says about one field, shown under it in the form
const FieldHint = ({ field }) => {
  if (field.verdict === "correct") {
    return (
      <div style={{ color: COLORS.correct, fontSize: 12, marginTop: 4 }}>
        ✓ {Localize("PAGES.AI_VERIFICATION.HINT.CORRECT")}{" "}
        <SourceLink url={field.evidence} />
      </div>
    );
  }
  const found =
    field.verdict === "incorrect" ? field.suggested : field.fillSuggestion;
  if (field.verdict === "incorrect" || found) {
    return (
      <div
        style={{
          color:
            field.verdict === "incorrect" ? COLORS.incorrect : COLORS.found,
          fontSize: 12,
          marginTop: 4,
        }}
      >
        <RobotOutlined />{" "}
        {field.verdict === "incorrect"
          ? Localize("PAGES.AI_VERIFICATION.HINT.SUGGESTS")
          : Localize("PAGES.AI_VERIFICATION.HINT.FOUND")}
        {found ? (
          <>
            : <b>{found}</b>
          </>
        ) : (
          <> — {Localize("PAGES.AI_VERIFICATION.HINT.LOOKS_WRONG")}</>
        )}{" "}
        <SourceLink url={field.evidence} />
      </div>
    );
  }
  return null;
};

const countSuggestions = (v) =>
  v.fields.filter((f) => f.verdict === "incorrect" || f.fillSuggestion).length;

/**
 * Loads the latest AI check of one record and shares it with the banner and the form fields
 * below it. Put it around a whole detail page.
 * @param {{entity: "company"|"contact"|"brand", id: string}} props
 */
export const AiVerificationProvider = ({ entity, id, children }) => {
  const [verification, setVerification] = useState(null);
  const [shown, setShown] = useState(false);
  const [busy, setBusy] = useState(null); // "verify" | "archive"
  const [ready, setReady] = useState(false);
  // false when the user may not see AI checks at all (the feature then stays out of the way)
  const [available, setAvailable] = useState(true);

  const load = useCallback(async () => {
    try {
      setVerification(await getAiVerificationApi({ entity, id }));
      setAvailable(true);
    } catch (e) {
      // the banner is optional: it must never get in the way of the page itself
      setAvailable(false);
    } finally {
      setReady(true);
    }
  }, [entity, id]);

  useEffect(() => {
    setShown(false);
    setVerification(null);
    setReady(false);
    if (id) load();
  }, [id, load]);

  const verify = async () => {
    setBusy("verify");
    try {
      setVerification(await verifyWithAiApi({ entity, id }));
      setShown(true);
    } catch (error) {
      window.setError && window.setError(error);
    } finally {
      setBusy(null);
    }
  };

  const archive = async () => {
    setBusy("archive");
    try {
      await archiveAiVerificationApi(verification._id);
      setVerification(null);
      setShown(false);
    } catch (error) {
      window.setError && window.setError(error);
    } finally {
      setBusy(null);
    }
  };

  const hints = useMemo(() => {
    if (!shown || !verification) return {};
    return verification.fields.reduce(
      (acc, f) => ({ ...acc, [f.field]: <FieldHint field={f} /> }),
      {},
    );
  }, [shown, verification]);

  const value = {
    verification,
    shown,
    setShown,
    busy,
    ready,
    available,
    verify,
    archive,
    hints,
  };
  return <AiContext.Provider value={value}>{children}</AiContext.Provider>;
};

const ResultsTable = ({ verification }) => (
  <Table
    size="small"
    pagination={false}
    rowKey="field"
    style={{ marginTop: 12 }}
    dataSource={verification.fields}
    columns={[
      {
        title: Localize("PAGES.DATA_QUALITY.COLUMNS.FIELD"),
        dataIndex: "field",
        render: (k) => Localize(`PAGES.DATA_QUALITY.FIELDS.${k}`),
      },
      {
        title: Localize("PAGES.DATA_QUALITY.ACCURACY.STORED"),
        dataIndex: "stored",
        render: (v) =>
          v === undefined || v === null || v === "" ? "-" : String(v),
      },
      {
        title: Localize("PAGES.DATA_QUALITY.ACCURACY.VERDICT.TITLE"),
        dataIndex: "verdict",
        render: (v) => (
          <Tag color={VERDICT_COLORS[v]}>
            {Localize(`PAGES.DATA_QUALITY.ACCURACY.VERDICT.${v}`)}
          </Tag>
        ),
      },
      {
        title: Localize("PAGES.DATA_QUALITY.ACCURACY.SUGGESTED"),
        render: (_, f) => f.suggested || f.fillSuggestion || "-",
      },
      {
        title: Localize("PAGES.DATA_QUALITY.ACCURACY.EVIDENCE"),
        dataIndex: "evidence",
        render: (e) => (isUrl(e) ? <SourceLink url={e} /> : "-"),
      },
    ]}
  />
);

/**
 * On top of a detail page, inside AiVerificationProvider: what the AI found out about this
 * record (with show/hide of the details, and archive), or just a button to ask for a check.
 */
export const AiVerificationBanner = () => {
  const {
    verification,
    shown,
    setShown,
    busy,
    ready,
    available,
    verify,
    archive,
  } = useContext(AiContext);
  if (!ready || !available) return null;

  if (!verification) {
    return (
      <div style={{ textAlign: "right", marginBottom: 8 }}>
        <Button
          size="small"
          icon={<RobotOutlined />}
          loading={busy === "verify"}
          onClick={verify}
        >
          {Localize("PAGES.AI_VERIFICATION.VERIFY")}
        </Button>
        {busy === "verify" && (
          <Text type="secondary" style={{ marginLeft: 8 }}>
            {Localize("PAGES.AI_VERIFICATION.VERIFYING")}
          </Text>
        )}
      </div>
    );
  }

  const suggestions = countSuggestions(verification);
  const { correct, incorrect, unverifiable } = verification.counts;
  return (
    <Card
      size="small"
      style={{
        marginBottom: 12,
        borderLeft: `4px solid ${suggestions ? "#faad14" : COLORS.correct}`,
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          gap: 12,
          flexWrap: "wrap",
          alignItems: "center",
        }}
      >
        <div>
          <div>
            <RobotOutlined />{" "}
            <b>
              {Localize("PAGES.AI_VERIFICATION.VERIFIED_ON", [
                moment(verification.createdAt).format("DD/MM/YYYY HH:mm"),
              ])}
            </b>{" "}
            <Tag>
              {Localize(
                `PAGES.AI_VERIFICATION.SOURCE_OF.${verification.source}`,
              )}
            </Tag>
            {verification.requestedBy && verification.requestedBy.name && (
              <Text type="secondary">
                {Localize("PAGES.AI_VERIFICATION.REQUESTED_BY", [
                  verification.requestedBy.name,
                ])}
              </Text>
            )}
          </div>
          <div>
            {verification.accuracyPct === null
              ? Localize("PAGES.AI_VERIFICATION.NOTHING_VERIFIED")
              : Localize("PAGES.AI_VERIFICATION.ACCURACY", [
                  verification.accuracyPct,
                ])}{" "}
            {Localize("PAGES.AI_VERIFICATION.SUMMARY", [
              correct,
              incorrect,
              unverifiable,
            ])}{" "}
            {suggestions > 0 && (
              <Text strong>
                {Localize("PAGES.AI_VERIFICATION.SUGGESTIONS", [suggestions])}
              </Text>
            )}
          </div>
        </div>
        <Space>
          <Button
            size="small"
            type={shown ? "default" : "primary"}
            onClick={() => setShown(!shown)}
          >
            {shown
              ? Localize("PAGES.AI_VERIFICATION.HIDE")
              : Localize("PAGES.AI_VERIFICATION.SHOW")}
          </Button>
          <Popconfirm
            title={Localize("PAGES.AI_VERIFICATION.ARCHIVE_CONFIRM")}
            onConfirm={archive}
          >
            <Button size="small" loading={busy === "archive"}>
              {Localize("PAGES.AI_VERIFICATION.ARCHIVE")}
            </Button>
          </Popconfirm>
          <Button
            size="small"
            icon={<RobotOutlined />}
            loading={busy === "verify"}
            onClick={verify}
          >
            {Localize("PAGES.AI_VERIFICATION.VERIFY_AGAIN")}
          </Button>
        </Space>
      </div>
      {shown && <ResultsTable verification={verification} />}
    </Card>
  );
};
